import { Document, DocumentFilter } from '@/features/documents/types';

export interface CacheStats {
  totalDocuments: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  lastUpdated: number;
  memoryUsage: number; // estimated in bytes
}

export interface CacheEntry {
  document: Document;
  lastAccessed: number;
  source: 'api' | 'cache' | 'localStorage';
}

export interface PaginatedCacheResult {
  documents: Document[];
  total: number;
  currentPage: number;
  totalPages: number;
  fromCache: boolean;
  cacheStats?: CacheStats;
}

/**
 * DocumentCacheService - Manages caching of documents for improved performance
 * 
 * Features:
 * - Memory-based caching with Map structure for O(1) lookups
 * - localStorage persistence for cross-session caching
 * - Cache warming strategy to pre-load all documents
 * - Smart invalidation and cache management
 * - Performance statistics and debugging
 */
export class DocumentCacheService {
  private cache = new Map<string, CacheEntry>();
  private cacheStats: CacheStats = {
    totalDocuments: 0,
    cacheHits: 0,
    cacheMisses: 0,
    hitRate: 0,
    lastUpdated: 0,
    memoryUsage: 0
  };
  
  // Configuration
  private readonly CACHE_KEY = 'obta_documents_cache';
  private readonly CACHE_STATS_KEY = 'obta_cache_stats';
  private readonly MAX_CACHE_SIZE = 1000; // Maximum number of documents to cache
  private readonly CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  private readonly STORAGE_VERSION = '1.0';
  
  // Pagination settings
  private readonly DEFAULT_PAGE_SIZE = 20;
  
  constructor() {
    this.loadFromLocalStorage();
    this.cleanExpiredEntries();
  }

  /**
   * Get documents for a specific page with caching
   */
  async getDocumentsPage(
    page: number = 1, 
    pageSize: number = this.DEFAULT_PAGE_SIZE,
    filters?: DocumentFilter
  ): Promise<PaginatedCacheResult> {
    const startTime = performance.now();
    
    try {
      // Check if we have all documents cached and can serve from cache
      if (this.canServeFromCache(filters)) {
        console.log('📦 Serving documents from cache');
        return this.getPageFromCache(page, pageSize, filters);
      }

      console.log('🌐 Cache miss - fetching from API');
      this.cacheStats.cacheMisses++;
      
      // If not in cache, this will be handled by the enhanced DocumentsService
      // For now, return an indicator that we need to fetch from API
      return {
        documents: [],
        total: 0,
        currentPage: page,
        totalPages: 0,
        fromCache: false,
        cacheStats: this.getStats()
      };
    } finally {
      const duration = performance.now() - startTime;
      console.log(`⏱️ getDocumentsPage took ${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Add documents to cache (typically from API responses)
   */
  addDocuments(documents: Document[]): void {
    const now = Date.now();
    
    documents.forEach(doc => {
      const entry: CacheEntry = {
        document: doc,
        lastAccessed: now,
        source: 'api'
      };
      
      this.cache.set(doc.id, entry);
    });
    
    this.cacheStats.totalDocuments = this.cache.size;
    this.cacheStats.lastUpdated = now;
    this.updateMemoryUsage();
    
    // Persist to localStorage
    this.saveToLocalStorage();
    
    console.log(`📥 Added ${documents.length} documents to cache. Total: ${this.cache.size}`);
  }

  /**
   * Get a single document from cache
   */
  getDocument(id: string): Document | null {
    const entry = this.cache.get(id);
    
    if (entry) {
      // Update last accessed time
      entry.lastAccessed = Date.now();
      this.cacheStats.cacheHits++;
      
      console.log(`🎯 Cache hit for document ${id}`);
      return entry.document;
    }
    
    this.cacheStats.cacheMisses++;
    console.log(`❌ Cache miss for document ${id}`);
    return null;
  }

  /**
   * Update a document in cache
   */
  updateDocument(document: Document): void {
    const existing = this.cache.get(document.id);
    
    if (existing) {
      existing.document = document;
      existing.lastAccessed = Date.now();
      existing.source = 'api'; // Mark as fresh from API
      
      this.cacheStats.lastUpdated = Date.now();
      this.saveToLocalStorage();
      
      console.log(`🔄 Updated document ${document.id} in cache`);
    } else {
      // If not in cache, add it
      this.addDocuments([document]);
    }
  }

  /**
   * Remove a document from cache
   */
  removeDocument(id: string): void {
    if (this.cache.delete(id)) {
      this.cacheStats.totalDocuments = this.cache.size;
      this.cacheStats.lastUpdated = Date.now();
      this.saveToLocalStorage();
      
      console.log(`🗑️ Removed document ${id} from cache`);
    }
  }

  /**
   * Clear all cached documents
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheStats = {
      totalDocuments: 0,
      cacheHits: 0,
      cacheMisses: 0,
      hitRate: 0,
      lastUpdated: Date.now(),
      memoryUsage: 0
    };
    
    // Clear localStorage
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_STATS_KEY);
    
    console.log('🧹 Cache cleared');
  }

  /**
   * Warm the cache by pre-loading all documents
   */
  async warmCache(): Promise<void> {
    console.log('🔥 Starting cache warming...');
    
    try {
      // This will be implemented when we integrate with the enhanced DocumentsService
      // For now, just a placeholder
      console.log('🔥 Cache warming will be implemented in Phase 3');
    } catch (error) {
      console.error('❌ Cache warming failed:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const totalQueries = this.cacheStats.cacheHits + this.cacheStats.cacheMisses;
    this.cacheStats.hitRate = totalQueries > 0 ? this.cacheStats.cacheHits / totalQueries : 0;
    
    return { ...this.cacheStats };
  }

  /**
   * Check if we can serve a request from cache
   */
  private canServeFromCache(filters?: DocumentFilter): boolean {
    // For now, we can only serve from cache if no filters are applied
    // and we have a reasonable number of documents cached
    const hasMinimumDocuments = this.cache.size >= 100; // At least 100 docs cached
    const noComplexFilters = !filters || this.isSimpleFilter(filters);
    
    return hasMinimumDocuments && noComplexFilters;
  }

  /**
   * Check if filters are simple enough to handle in-memory
   */
  private isSimpleFilter(filters: DocumentFilter): boolean {
    // Simple filters that we can handle in-memory
    const hasOnlyBasicFilters = 
      !filters.date_range &&
      !filters.categories &&
      !filters.tags &&
      (!filters.search_term || filters.search_term.length < 50); // Simple text search only
    
    return hasOnlyBasicFilters;
  }

  /**
   * Get a page of documents from cache
   */
  private getPageFromCache(
    page: number, 
    pageSize: number, 
    filters?: DocumentFilter
  ): PaginatedCacheResult {
    let documents = Array.from(this.cache.values()).map(entry => entry.document);
    
    // Apply filters in-memory
    if (filters) {
      documents = this.applyFiltersInMemory(documents, filters);
    }
    
    // Sort documents (default by created_at desc)
    documents.sort((a, b) => b.audit.created_at - a.audit.created_at);
    
    // Apply pagination
    const total = documents.length;
    const totalPages = Math.ceil(total / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageDocuments = documents.slice(startIndex, endIndex);
    
    this.cacheStats.cacheHits++;
    
    return {
      documents: pageDocuments,
      total,
      currentPage: page,
      totalPages,
      fromCache: true,
      cacheStats: this.getStats()
    };
  }

  /**
   * Apply filters to documents in memory
   */
  private applyFiltersInMemory(documents: Document[], filters: DocumentFilter): Document[] {
    return documents.filter(doc => {
      // Document type filter
      if (filters.document_type && filters.document_type.length > 0) {
        if (!filters.document_type.includes(doc.document_type)) {
          return false;
        }
      }
      
      // Visibility filter
      if (filters.visibility && filters.visibility.length > 0) {
        if (!filters.visibility.includes(doc.visibility)) {
          return false;
        }
      }
      
      // Simple text search
      if (filters.search_term) {
        const searchTerm = filters.search_term.toLowerCase();
        const searchableText = [
          doc.title,
          doc.description,
          doc.creator,
          doc.ocr_text
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableText.includes(searchTerm)) {
          return false;
        }
      }
      
      return true;
    });
  }

  /**
   * Clean expired cache entries
   */
  private cleanExpiredEntries(): void {
    const now = Date.now();
    let removedCount = 0;
    
    for (const [id, entry] of this.cache.entries()) {
      if (now - entry.lastAccessed > this.CACHE_TTL) {
        this.cache.delete(id);
        removedCount++;
      }
    }
    
    if (removedCount > 0) {
      this.cacheStats.totalDocuments = this.cache.size;
      console.log(`🧹 Cleaned ${removedCount} expired cache entries`);
    }
  }

  /**
   * Update memory usage estimation
   */
  private updateMemoryUsage(): void {
    // Rough estimation of memory usage
    let totalSize = 0;
    
    for (const entry of this.cache.values()) {
      // Estimate size based on JSON string length
      totalSize += JSON.stringify(entry.document).length * 2; // UTF-16 characters
    }
    
    this.cacheStats.memoryUsage = totalSize;
  }

  /**
   * Save cache to localStorage
   */
  private saveToLocalStorage(): void {
    try {
      // Only save if cache is not too large
      if (this.cache.size > this.MAX_CACHE_SIZE) {
        console.warn('⚠️ Cache too large for localStorage, skipping save');
        return;
      }
      
      const cacheData = {
        version: this.STORAGE_VERSION,
        timestamp: Date.now(),
        entries: Array.from(this.cache.entries())
      };
      
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
      localStorage.setItem(this.CACHE_STATS_KEY, JSON.stringify(this.cacheStats));
      
      console.log(`💾 Saved ${this.cache.size} documents to localStorage`);
    } catch (error) {
      console.error('❌ Failed to save cache to localStorage:', error);
      // If localStorage is full, try to clear old data
      if (error instanceof DOMException && error.code === 22) {
        console.log('💾 localStorage full, clearing cache');
        this.clearCache();
      }
    }
  }

  /**
   * Load cache from localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const cachedDataString = localStorage.getItem(this.CACHE_KEY);
      const cachedStatsString = localStorage.getItem(this.CACHE_STATS_KEY);
      
      if (cachedDataString) {
        const cachedData = JSON.parse(cachedDataString);
        
        // Check version compatibility
        if (cachedData.version !== this.STORAGE_VERSION) {
          console.log('🔄 Cache version mismatch, clearing cache');
          this.clearCache();
          return;
        }
        
        // Check if cache is not too old
        const age = Date.now() - cachedData.timestamp;
        if (age > this.CACHE_TTL) {
          console.log('⏰ Cache expired, clearing cache');
          this.clearCache();
          return;
        }
        
        // Restore cache
        this.cache = new Map(cachedData.entries);
        
        // Restore stats
        if (cachedStatsString) {
          this.cacheStats = JSON.parse(cachedStatsString);
        }
        
        this.updateMemoryUsage();
        console.log(`📂 Loaded ${this.cache.size} documents from localStorage`);
      }
    } catch (error) {
      console.error('❌ Failed to load cache from localStorage:', error);
      this.clearCache();
    }
  }

  /**
   * Get debug information about the cache
   */
  getDebugInfo(): any {
    const documents = Array.from(this.cache.values());
    const sourceBreakdown = documents.reduce((acc, entry) => {
      acc[entry.source] = (acc[entry.source] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const oldestEntry = documents.reduce((oldest, entry) => 
      entry.lastAccessed < oldest.lastAccessed ? entry : oldest
    , documents[0]);
    
    const newestEntry = documents.reduce((newest, entry) => 
      entry.lastAccessed > newest.lastAccessed ? entry : newest
    , documents[0]);
    
    return {
      cacheStats: this.getStats(),
      sourceBreakdown,
      oldestEntry: oldestEntry ? {
        id: oldestEntry.document.id,
        title: oldestEntry.document.title,
        lastAccessed: new Date(oldestEntry.lastAccessed).toISOString()
      } : null,
      newestEntry: newestEntry ? {
        id: newestEntry.document.id,
        title: newestEntry.document.title,
        lastAccessed: new Date(newestEntry.lastAccessed).toISOString()
      } : null,
      memoryUsageFormatted: this.formatBytes(this.cacheStats.memoryUsage),
      localStorageUsage: this.getLocalStorageUsage()
    };
  }

  /**
   * Format bytes for human-readable display
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get localStorage usage information
   */
  private getLocalStorageUsage(): any {
    try {
      const cacheSize = localStorage.getItem(this.CACHE_KEY)?.length || 0;
      const statsSize = localStorage.getItem(this.CACHE_STATS_KEY)?.length || 0;
      const totalSize = cacheSize + statsSize;
      
      return {
        cacheSize: this.formatBytes(cacheSize * 2), // UTF-16
        statsSize: this.formatBytes(statsSize * 2),
        totalSize: this.formatBytes(totalSize * 2)
      };
    } catch {
      return { error: 'Unable to calculate localStorage usage' };
    }
  }
}

// Export singleton instance
export const documentCacheService = new DocumentCacheService();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).documentCacheService = documentCacheService;
}
